import { useEffect, useMemo, useState } from "react";
import type { NavigationProp } from "@react-navigation/native";
import {
  Image,
  View,
  Pressable,
  Platform,
  useWindowDimensions,
} from "react-native";
import { Building2, Headphones, Sparkles } from "lucide-react-native";
import type { LucideIcon } from "lucide-react-native";
import { useSharedValue } from "react-native-reanimated";
import {
  CloseButton,
  EmptyStateSubtitleText,
  EmptyStateTitleText,
  StatusBadge,
} from "@louise/moon-ui";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import { HEADER_INSET } from "@louise/moon-ui/theme/tokens";
import { useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { useActiveCycle } from "@src/packages/cycles/hooks/useCycles";
import { useSupportConversation } from "@src/packages/support/hooks/useSupportConversation";
import { EMBER } from "../../../onboarding/theme";
import { AiAssistantCard } from "../../components/AiAssistantCard";
import type { ChatStackParamList, MainTabParamList } from "../../types";
import { semanticColors } from "@louise/theme/tokens";

type ChatMode = "ai" | "care" | "support";

const CARE_TEAM_ILLUSTRATION =
  require("@src/apps/moon/assets/images/you-icons/connections-row-3d.png") as number;

type Props = {
  onClose?: () => void;
  route?: {
    params?: {
      initialPrompt?: string;
      autoSendInitialPrompt?: boolean;
      returnTab?: "Costs";
    };
  };
  navigation?: NavigationProp<ChatStackParamList, "Assistant">;
};

type ModeDef = {
  id: ChatMode;
  label: string;
  icon: LucideIcon;
};

export default function ChatHubScreen({ onClose, route, navigation }: Props) {
  const t = useTranslations();
  const { width } = useWindowDimensions();
  const { data: activeCycle } = useActiveCycle();
  const [activeMode, setActiveMode] = useState<ChatMode>("ai");
  const chatProgress = useSharedValue(1);
  const { conversation: supportConversation, sendMessage: sendSupportMessage } =
    useSupportConversation(activeMode === "support");
  const initialPrompt = route?.params?.initialPrompt;
  const autoSendInitialPrompt = route?.params?.autoSendInitialPrompt === true;
  const clinicName = activeCycle?.provider?.trim() || null;
  const clinicIsConnected = Boolean(activeCycle?.providerId);
  const closeChat = () => {
    if (onClose) {
      onClose();
      return;
    }

    const rootNavigation =
      navigation?.getParent<NavigationProp<MainTabParamList>>();
    if (route?.params?.returnTab === "Costs" && rootNavigation) {
      rootNavigation?.navigate("Costs", { screen: "EmployerBenefits" });
      return;
    }

    if (navigation?.canGoBack()) {
      navigation.goBack();
      return;
    }

    rootNavigation?.navigate("Home");
  };
  const isCompactHeader =
    Platform.OS === "web" ? window.innerWidth < 480 : width < 480;
  const modes = useMemo<ModeDef[]>(() => {
    const availableModes: ModeDef[] = [
      { id: "ai", label: "AI", icon: Sparkles },
      {
        id: "care",
        label: t("chat.hub.mode_care_label"),
        icon: Building2,
      },
      {
        id: "support",
        label: t("chat.hub.mode_support_label"),
        icon: Headphones,
      },
    ];
    return availableModes;
  }, [t]);

  useEffect(() => {
    if (!modes.some((mode) => mode.id === activeMode)) {
      setActiveMode(modes[0]?.id ?? "care");
    }
  }, [activeMode, modes]);

  const modeCopy = useMemo(() => {
    switch (activeMode) {
      case "care":
        return {
          title: t("chat.hub.care_title"),
          subtitle: t("chat.hub.care_subtitle"),
          emptyPrompt: t("chat.hub.care_empty_prompt"),
          inputPlaceholder: t("chat.hub.care_input_placeholder"),
          inputDisabled: true,
        };
      case "support":
        return {
          title: t("chat.hub.support_title"),
          subtitle: t("chat.hub.support_subtitle"),
          emptyPrompt: t("chat.hub.support_empty_prompt"),
          inputPlaceholder: t("chat.hub.support_input_placeholder"),
          inputDisabled: false,
        };
      case "ai":
      default:
        return {
          title: t("chat.hub.ai_title"),
          subtitle: t("chat.hub.ai_subtitle"),
          emptyPrompt: t("chat.hub.ai_empty_prompt"),
          inputPlaceholder: t("chat.hub.ai_input_placeholder"),
          inputDisabled: false,
        };
    }
  }, [activeMode, t]);

  const handleSupportSend = (text: string) => {
    if (sendSupportMessage.isPending) return;
    sendSupportMessage.mutate({
      content: text,
      entryPoint: "support",
      attachments: [],
    });
  };

  const careEmptyContent = (
    <CareModeEmptyState
      clinicName={clinicName}
      clinicIsConnected={clinicIsConnected}
    />
  );

  const supportMessages =
    activeMode === "support"
      ? (supportConversation.data?.messages ?? [])
      : undefined;
  const supportError =
    sendSupportMessage.error instanceof Error
      ? sendSupportMessage.error.message
      : supportConversation.error instanceof Error
        ? supportConversation.error.message
        : null;

  const modeTabs = (
    <ModeTabs modes={modes} activeMode={activeMode} onChange={setActiveMode} />
  );

  return (
    <GlassScreenLayout transparent={Boolean(onClose)}>
      <View
        style={{
          flex: 1,
          paddingHorizontal: 16,
          paddingTop: onClose ? 12 : HEADER_INSET,
          paddingBottom: onClose ? 24 : 100,
          maxWidth: onClose ? 520 : 620,
          width: "100%",
          alignSelf: "center",
        }}
      >
        <AiAssistantCard
          expanded
          onToggle={() => {}}
          progress={chatProgress}
          returnTab="Chat"
          variant="screen"
          showTitle={!isCompactHeader && !onClose}
          title={modeCopy.title}
          subtitle={modeCopy.subtitle}
          emptyPrompt={modeCopy.emptyPrompt}
          emptyContent={activeMode === "care" ? careEmptyContent : undefined}
          supportMessages={supportMessages}
          supportTeamLabel={t("chat.hub.support_team_label")}
          supportLoading={
            activeMode === "support" && supportConversation.isPending
          }
          supportError={activeMode === "support" ? supportError : null}
          supportLoadFailure={
            activeMode === "support" && supportConversation.isError
              ? {
                  onRetry: supportConversation.refetch,
                  isRetrying: supportConversation.isFetching,
                }
              : null
          }
          inputPlaceholder={modeCopy.inputPlaceholder}
          inputDisabled={modeCopy.inputDisabled}
          initialInputText={activeMode === "ai" ? initialPrompt : undefined}
          initialAutoSend={activeMode === "ai" ? autoSendInitialPrompt : false}
          showEmptyOrb
          showTopOrb={Boolean(onClose)}
          onSend={activeMode === "support" ? handleSupportSend : undefined}
          headerLeft={navigation || onClose ? null : undefined}
          showSubtitle={!onClose}
          headerClose={
            navigation || onClose ? (
              <CloseChatButton onPress={closeChat} />
            ) : null
          }
          headerRight={
            onClose ? modeTabs : isCompactHeader ? undefined : modeTabs
          }
          headerBottom={
            onClose ? undefined : isCompactHeader ? modeTabs : undefined
          }
        />
      </View>
    </GlassScreenLayout>
  );
}

function CareModeEmptyState({
  clinicName,
  clinicIsConnected,
}: {
  clinicName: string | null;
  clinicIsConnected: boolean;
}) {
  const t = useTranslations();
  const body = clinicIsConnected
    ? t("chat.hub.care_connected_empty_prompt", {
        params: { clinic: clinicName ?? t("chat.hub.care_team_fallback") },
      })
    : clinicName
      ? t("chat.hub.care_clinic_empty_prompt", {
          params: { clinic: clinicName },
        })
      : t("chat.hub.care_empty_prompt");

  return (
    <View
      testID="care-connection-empty-state"
      style={{ alignItems: "center", gap: 12, width: "100%" }}
    >
      <Image
        testID="care-team-illustration"
        source={CARE_TEAM_ILLUSTRATION}
        resizeMode="contain"
        accessible={false}
        style={{ width: 88, height: 88, marginBottom: 2 }}
      />
      <StatusBadge type="coming-soon" />
      <View style={{ alignItems: "center", gap: 4 }}>
        <EmptyStateTitleText variant="featured" style={{ textAlign: "center" }}>
          {t("chat.hub.care_empty_title")}
        </EmptyStateTitleText>
        <EmptyStateSubtitleText style={{ textAlign: "center", marginTop: 0 }}>
          {body}
        </EmptyStateSubtitleText>
      </View>
    </View>
  );
}

function CloseChatButton({ onPress }: { onPress: () => void }) {
  const t = useTranslations();
  return (
    <CloseButton
      onPress={onPress}
      accessibilityLabel={t("chat.hub.close_chat")}
      action="chat.hub.close_chat"
    />
  );
}

function ModeTabs({
  modes,
  activeMode,
  onChange,
}: {
  modes: ModeDef[];
  activeMode: ChatMode;
  onChange: (mode: ChatMode) => void;
}) {
  const capturePress = usePressCapture("ModeTabs");
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: 2,
        height: 32,
        padding: 2,
        borderRadius: 999,
        backgroundColor: semanticColors.action.quiet.background,
        borderWidth: 1,
        borderColor: semanticColors.border.glass,
      }}
    >
      {modes.map((mode) => {
        const selected = activeMode === mode.id;
        const Icon = mode.icon;
        return (
          <Pressable
            key={mode.id}
            onPress={() => {
              // mode.label is translated, so the funnel keys off the stable
              // mode id instead of the visible copy.
              capturePress(pressLabel(mode.label), `chat_mode_${mode.id}`);
              onChange(mode.id);
            }}
            accessibilityRole="tab"
            accessibilityState={{ selected }}
            aria-selected={selected}
            accessibilityLabel={mode.label}
            style={{
              width: 32,
              height: 26,
              borderRadius: 999,
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: selected
                ? "rgba(255,255,255,0.86)"
                : "transparent",
              ...(Platform.OS === "web"
                ? ({ cursor: "pointer" } as Record<string, string>)
                : null),
            }}
          >
            <Icon
              size={13}
              color={selected ? EMBER.colors.text : EMBER.colors.textMid}
              strokeWidth={selected ? 2 : 1.7}
            />
          </Pressable>
        );
      })}
    </View>
  );
}
